// src/app/shared/directives/textarea-autosize.directive.ts
import { Directive, ElementRef, HostListener, AfterContentInit } from '@angular/core';

@Directive({
  standalone: true,
  selector: '[appTextareaAutosize]'
})
export class TextareaAutosizeDirective implements AfterContentInit {

  constructor(private elementRef: ElementRef<HTMLTextAreaElement>) {}

  @HostListener('input')
  onInput(): void {
    this.adjust();
  }

  ngAfterContentInit(): void {
    // A small delay ensures that initial content (e.g., from ngModel) is fully rendered.
    setTimeout(() => this.adjust(), 100);
  }

  /**
   * Finds the first parent element designated to handle scrolling.
   *
   * This version is more robust because it doesn't check if the parent is *currently*
   * scrollable (scrollHeight > clientHeight). It only checks if it's *configured*
   * to be scrollable (overflow-y is 'auto' or 'scroll'). This correctly handles cases
   * where the textarea's growth is what *causes* the parent to become scrollable.
   */
  private findScrollableParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        // We found the designated scroll container, return it immediately.
        return parent;
      }
      // If this parent is the document body, stop searching.
      if (parent === document.body) {
        return null;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private adjust(): void {
    const element = this.elementRef.nativeElement;
    const scrollableParent = this.findScrollableParent(element);

    // --- CAPTURE SCROLL POSITIONS ---
    const oldWindowScrollY = window.scrollY;
    // This will correctly get scrollTop (which is 0 if not yet scrolled)
    const oldParentScrollTop = scrollableParent ? scrollableParent.scrollTop : 0;

    // --- PERFORM THE HEIGHT ADJUSTMENT ---
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight + 4}px`;

    // --- RESTORE SCROLL POSITIONS ---
    requestAnimationFrame(() => {
      if (scrollableParent) {
        scrollableParent.scrollTop = oldParentScrollTop;
      }
      window.scrollTo(window.scrollX, oldWindowScrollY);
    });
  }
}
